/**
 * Utility functions for generating fumigation schedule dates
 * Converted from legacy C# logic in legacy-dates.cs
 */

interface DateOption {
    label: string; // Display format: "ddd, MMM dd" (e.g., "Mon, Jan 15")
    value: string; // Value format: "MM/dd/yyyy" (e.g., "01/15/2024")
}

interface FumigationDates {
    closeDates: DateOption[];
    restoreDates: DateOption[];
}

/**
 * Calculate US Federal Holidays for the current year
 */
function calculateHolidays(year: number): Set<string> {
    const holidays = new Set<string>();

    // Helper to format date as "yyyy-MM-dd"
    const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper to get day of week (0 = Sunday, 6 = Saturday)
    const getDayOfWeek = (date: Date): number => date.getDay();

    // New Year's Day (Jan 1) - If Sunday, observed Monday
    let newYears = new Date(year, 0, 1);
    if (getDayOfWeek(newYears) === 0) { // Sunday
        newYears = new Date(year, 0, 2);
    }
    holidays.add(formatDate(newYears));

    // MLK Day - 3rd Monday of January
    let janFirstMonday = new Date(year, 0, 1);
    const janDayOfWeek = getDayOfWeek(janFirstMonday);
    const daysUntilMonday = janDayOfWeek === 0 ? 1 : (8 - janDayOfWeek) % 7;
    janFirstMonday.setDate(janFirstMonday.getDate() + daysUntilMonday);
    
    if (janFirstMonday < new Date(year, 0, 1)) {
        holidays.add(formatDate(new Date(janFirstMonday.getTime() + 21 * 24 * 60 * 60 * 1000)));
    } else {
        holidays.add(formatDate(new Date(janFirstMonday.getTime() + 14 * 24 * 60 * 60 * 1000)));
    }

    // Presidents Day - 3rd Monday of February
    let febFirstMonday = new Date(year, 1, 1);
    const febDayOfWeek = getDayOfWeek(febFirstMonday);
    const febDaysUntilMonday = febDayOfWeek === 0 ? 1 : (8 - febDayOfWeek) % 7;
    febFirstMonday.setDate(febFirstMonday.getDate() + febDaysUntilMonday);
    
    if (febFirstMonday < new Date(year, 1, 1)) {
        holidays.add(formatDate(new Date(febFirstMonday.getTime() + 21 * 24 * 60 * 60 * 1000)));
    } else {
        holidays.add(formatDate(new Date(febFirstMonday.getTime() + 14 * 24 * 60 * 60 * 1000)));
    }

    // Memorial Day - Last Monday of May
    let mayLastMonday = new Date(year, 4, 31);
    const mayDayOfWeek = getDayOfWeek(mayLastMonday);
    const daysBackToMonday = (mayDayOfWeek + 6) % 7;
    mayLastMonday.setDate(mayLastMonday.getDate() - daysBackToMonday);
    
    if (mayLastMonday.getMonth() === 4) {
        holidays.add(formatDate(mayLastMonday));
    } else {
        mayLastMonday.setDate(mayLastMonday.getDate() - 7);
        holidays.add(formatDate(mayLastMonday));
    }

    // Independence Day (July 4) - If Saturday, observed Friday; If Sunday, observed Monday
    let independenceDay = new Date(year, 6, 4);
    const indepDayOfWeek = getDayOfWeek(independenceDay);
    if (indepDayOfWeek === 6) { // Saturday
        independenceDay = new Date(year, 6, 3);
    } else if (indepDayOfWeek === 0) { // Sunday
        independenceDay = new Date(year, 6, 5);
    }
    holidays.add(formatDate(independenceDay));

    // Labor Day - 1st Monday of September
    let sepFirstMonday = new Date(year, 8, 1);
    const sepDayOfWeek = getDayOfWeek(sepFirstMonday);
    const sepDaysUntilMonday = sepDayOfWeek === 0 ? 1 : (8 - sepDayOfWeek) % 7;
    sepFirstMonday.setDate(sepFirstMonday.getDate() + sepDaysUntilMonday);
    holidays.add(formatDate(sepFirstMonday));

    // Veterans Day (Nov 11) - If Sunday, observed Monday
    let veteransDay = new Date(year, 10, 11);
    if (getDayOfWeek(veteransDay) === 0) { // Sunday
        veteransDay = new Date(year, 10, 12);
    }
    holidays.add(formatDate(veteransDay));

    // Thanksgiving - 4th Thursday of November
    let novFirstThursday = new Date(year, 10, 1);
    const novDayOfWeek = getDayOfWeek(novFirstThursday);
    const daysUntilThursday = (11 - novDayOfWeek) % 7;
    novFirstThursday.setDate(novFirstThursday.getDate() + daysUntilThursday);
    
    const thanksgiving = new Date(novFirstThursday.getTime() + 21 * 24 * 60 * 60 * 1000);
    holidays.add(formatDate(thanksgiving));
    
    // Day after Thanksgiving
    const dayAfterThanksgiving = new Date(thanksgiving.getTime() + 24 * 60 * 60 * 1000);
    holidays.add(formatDate(dayAfterThanksgiving));

    // Christmas Eve & Christmas Day (Dec 24-25)
    let christmasEve = new Date(year, 11, 24);
    let christmasDay = new Date(year, 11, 25);
    const christmasDayOfWeek = getDayOfWeek(christmasEve);
    
    if (christmasDayOfWeek === 6) { // Saturday
        christmasEve = new Date(year, 11, 23);
        christmasDay = new Date(year, 11, 26);
    } else if (christmasDayOfWeek === 0) { // Sunday
        christmasEve = new Date(year, 11, 22);
    }
    
    holidays.add(formatDate(christmasEve));
    holidays.add(formatDate(christmasDay));

    return holidays;
}

/**
 * Generate fumigation date options (close and restore dates)
 * Based on legacy C# logic from legacy-dates.cs
 */
export function generateFumigationDates(): FumigationDates {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Get holidays for current year and next year (if in Sept-Dec)
    const holidays = calculateHolidays(currentYear);
    if (currentMonth >= 8) { // September or later (0-indexed)
        const nextYearHolidays = calculateHolidays(currentYear + 1);
        nextYearHolidays.forEach(h => holidays.add(h));
    }

    // Helper to format date for display
    const formatDateLabel = (date: Date): string => {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${days[date.getDay()]}, ${months[date.getMonth()]} ${String(date.getDate()).padStart(2, '0')}`;
    };

    // Helper to format date for value (MM/dd/yyyy)
    const formatDateValue = (date: Date): string => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };

    // Helper to format date as "yyyy-MM-dd" for holiday comparison
    const formatForComparison = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const closeDates: DateOption[] = [{ label: 'None', value: '' }];
    const restoreDates: DateOption[] = [{ label: 'None', value: '' }];

    // Determine start day offset based on current day of week
    const currentDay = now.getDay();
    let startCloseDay = 1;
    if (currentDay === 5 || currentDay === 6) { // Friday or Saturday
        startCloseDay = 2;
    }

    // Generate dates for next 90 days (Vista Requirement)
    for (let i = 1; i <= 90; i++) {
        const futureDate = new Date(now);
        futureDate.setDate(futureDate.getDate() + i);
        const dayOfWeek = futureDate.getDay();
        const dateStr = formatForComparison(futureDate);

        // Close dates: weekdays (Mon-Fri) only, exclude all holidays, start after offset
        if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not Saturday or Sunday
            if (!holidays.has(dateStr) && i > startCloseDay) {
                closeDates.push({
                    label: formatDateLabel(futureDate),
                    value: formatDateValue(futureDate)
                });
            }
        }

        // Restore dates: all days except Sunday, exclude major holidays only
        if (dayOfWeek !== 0) { // Not Sunday
            // Exclude: New Year's, Memorial Day, July 4th, Labor Day, Thanksgiving, day after Thanksgiving, Christmas
            const isRestrictedHoliday = holidays.has(dateStr) && (
                dateStr.endsWith('-01-01') || // New Year's
                dateStr.endsWith('-01-02') || // New Year's (observed)
                (futureDate.getMonth() === 4 && dayOfWeek === 1 && futureDate.getDate() >= 25) || // Memorial Day
                dateStr.includes('-07-04') || dateStr.includes('-07-03') || dateStr.includes('-07-05') || // July 4th
                (futureDate.getMonth() === 8 && dayOfWeek === 1 && futureDate.getDate() <= 7) || // Labor Day
                (futureDate.getMonth() === 10 && dayOfWeek === 4 && futureDate.getDate() >= 22 && futureDate.getDate() <= 28) || // Thanksgiving
                (futureDate.getMonth() === 10 && dayOfWeek === 5 && futureDate.getDate() >= 23 && futureDate.getDate() <= 29) || // Day after Thanksgiving
                dateStr.endsWith('-12-25') || dateStr.endsWith('-12-26') // Christmas
            );

            if (!isRestrictedHoliday) {
                restoreDates.push({
                    label: formatDateLabel(futureDate),
                    value: formatDateValue(futureDate)
                });
            }
        }
    }

    return { closeDates, restoreDates };
}

/**
 * Get formatted date options for use in dropdowns
 */
export function getCloseDateOptions(): DateOption[] {
    return generateFumigationDates().closeDates;
}

export function getRestoreDateOptions(): DateOption[] {
    return generateFumigationDates().restoreDates;
}
